import {
  TTrackTypes,
  ICartItemHeadless,
  TTermsAndConditionsContent,
  TMetaObjectBannerSection,
  TMetaObjectMainTrackConfig,
  TMetaObjectMiniTrackConfig,
  TUpsellModalConfig,
  TMetaObjectSeatsAndRacesConfig,
  TMembershipInfo,
  TBottomSummaryConfig,
  TDayTypeInterval,
} from "@/types";
import mainTrackImageBanner from "@/assets/images/main_track_.jpg";
import miniTrackImageBanner from "@/assets/images/miniTrack_.jpeg";
import { ITrackContextValue } from "./trackContextInterface";
import { isMainBookingPath } from "@/shared/utils/bookingUrl";
import { DEFAULT_CAPACITY_BANNER } from "@/shared/utils/capacityCopy";

type DefaultTrackSelection = {
  title: TTrackTypes;
  trackBanner: string;
};

export const getDefaultTrackSelection = (pathname: string = window.location.pathname): DefaultTrackSelection => {
  if (isMainBookingPath(pathname)) {
    return {
      title: "RACING_GAMES_TRACK",
      trackBanner: mainTrackImageBanner,
    };
  } else if (pathname.endsWith("/kids-go-karting-track") || /\d{7}mi$/.test(pathname)) {
    return {
      title: "RACING_GAMES_TRACK",
      trackBanner: miniTrackImageBanner,
    };
  } else if (pathname.endsWith("/junior-track") || /\d{6}jt$/.test(pathname)) {
    return {
      title: "RACING_GAMES_TRACK",
      trackBanner: mainTrackImageBanner,
    };
  } else {
    return {
      title: "RACING_GAMES_TRACK",
      trackBanner: mainTrackImageBanner,
    };
  }
};

// INITIAL_STATE shape is defined by `ITrackContextValue` in trackContextInterface.ts.
// The provider in TrackContext.tsx feeds this into useReducer.

export const INITIAL_STATE: ITrackContextValue = {
  activeKids: {
    optedIn: false,
    sessionId: null,
    totalBalanceCents: 0,
  },
  visitorLocation: {
    suburb: "",
    state: "",
    postcode: "",
    coordinates: {
      lat: 0,
      lng: 0,
    },
    distance: 0,
    discountAmount: 0,
  },
  isAnyItemsAvailableInCart: false,
  customerInfo: {
    isCustomerLogged: false,
    isCustomerActiveSubscriber: false,
    isCustomerAvenger: false,
    customerPostalCode: "",
  },
  membershipInfo: {
    isPopupOpen: false,
    isItemAddedToCart: false,
    isMembershipItemAddedToCart: false,
    isMembershipPriceEnabled: false,
    eligibleVariants: [],
  } as TMembershipInfo,
  isProcessing: false,
  processingType: "adding" as "adding" | "removing",
  isFirstVisit: true,
  hasProvidedPostcode: false,
  productRenderedLocation: "internal",
  appHostedAt: null,
  headlessCartInfo: {
    id: "",
    checkoutUrl: "",
  },
  headlessCartRemoveItem: {
    displayCartComfirmationPopup: false,
    shouldItemRemove: false,
    ItemId: "",
  },
  headlessCartTotalAmount: "",
  headlessCart: [] as ICartItemHeadless[],
  isNewsletterSubscripted: false,
  shopifyCartVariants: [],
  isShopifyCartVariantsLoaded: false,
  isInCartMode: false,
  isProductLoading: false,
  notification: {
    title: "",
    content: "",
    isVisible: false,
    variant: null,
  },
  isHeadlessMiniCartIsShowing: false,
  trackSelectionOption: getDefaultTrackSelection(),
  multiTrackQueue: {
    entries: [],
    currentIndex: 0,
  },
  isCartEmpty: true,
  hasSummaryBeenAutoShown: false,
  isSummaryExpanded: false,
  mainTrackSelectionByAgeGroup: {},

  mainTrackSelection: {
    ageGroup: null,
    disableDatesForUnderSixteenAgeGroup: [] as Date[],
    raceType: null,
    variantsAndDate: {
      selectedSessionTime: null,
      selectedDate: new Date(),
      selectedVariants: [],
      selectedVariantsForUpsell: [],
    },
    racesSeats: {
      numberOfSeats: 2,
      numberOfRaces: 3,
    },
    isTermsAndConditionExcepted: false,
    displayPopup: false,
    showInformationPopup: true,
  },
  juniorTrackSelection: {
    variantsAndDate: {
      selectedSessionTime: null,
      selectedDate: new Date(),
      selectedVariants: [],
      selectedVariantsForUpsell: [],
    },
    racesSeats: {
      numberOfSeats: 2,
      numberOfRaces: 3,
    },
    isTermsAndConditionExcepted: false,
  },
  intermediateTrackSelection: {
    variantsAndDate: {
      selectedSessionTime: null,
      selectedDate: new Date(),
      selectedVariants: [],
      selectedVariantsForUpsell: [],
    },
    racesSeats: {
      numberOfSeats: 2,
      numberOfRaces: 3,
    },
    isTermsAndConditionExcepted: false,
  },
  battleKartSelection: {
    variantsAndDate: {
      selectedSessionTime: null,
      selectedDate: new Date(),
      selectedVariants: [],
      selectedVariantsForUpsell: [],
    },
    racesSeats: {
      numberOfSeats: 2,
      numberOfRaces: 3,
    },
    isTermsAndConditionExcepted: false,
  },
  miniTrackSection: {
    variantsAndDate: {
      selectedSessionTime: null,
      selectedDate: new Date(),
      selectedVariants: [],
      selectedVariantsForUpsell: [],
    },
    racesSeats: {
      numberOfSeats: 2,
      numberOfRaces: 2,
    },
    isTermsAndConditionExcepted: false,
  },
  arcadeSelection: {
    arcadeGameChoose: {
      selectedArcadeGame: "10",
      selectedVariantId: "",
      isProductOutOfStock: false,
      selectedQuantity: 1,
    },
  },
  dispatch: () => {},
  termsAndConditionsContent: [] as TTermsAndConditionsContent[],
  mainTrackUpsellImageIds: [] as string[],
  miniTrackUpsellImageIds: [] as string[],
  mainTrackUpsellImageUrls: [],
  miniTrackUpsellImageUrls: [],
  progressiveBarBreadcrumbsRoutes: [] as string[],
  windowWidth: 0,
  metaObjectTrackSelectionConfig: {} as TMetaObjectBannerSection,
  bottomSummaryConfig: {
    viewLabel: "View Summary",
    hideLabel: "Hide Summary",
  } as TBottomSummaryConfig,
  dateTimeConfig: {
    heading: "For {track}, what day and time?",
    layoutMessage: "On {date} we are running our {layout}",
    capacityBanner: DEFAULT_CAPACITY_BANNER,
  },
  dayTypeIntervals: [] as TDayTypeInterval[],
  metaObjectMainTrackSectionConfig: {} as TMetaObjectMainTrackConfig,
  metaObjectMiniTrackSectionConfig: {} as TMetaObjectMiniTrackConfig,
  metaObjectJuniorTrackSectionConfig: {} as TMetaObjectSeatsAndRacesConfig,
  metaObjectIntermediateTrackSectionConfig: {} as TMetaObjectSeatsAndRacesConfig,
  metaObjectBattleKartTrackSectionConfig: {
    calenderAndTimeslotSection: {
      gamesConfig: {},
    },
  } as TMetaObjectSeatsAndRacesConfig,
  upsellModalConfig: {} as TUpsellModalConfig,
  collectionProducts: null,
  showArcadeBanner: false,
  navigationPreviousInfo: null,
  bottomSummaryNav: null,
};
